import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import style from "./style.scss";

import { FormattedMessage } from "react-intl";
import { getImageSizeUrls } from "../../../utils/fileUtils";
import { useIsMounted, usePreviousValue } from "../../../utils/hooks";
import { Clickable } from "../../button/Clickable";
import { FileSelector } from "../../form/Dropzone";
import { Icon } from "../../graphic/Icon";
import { ContentLoader } from "../../misc/ContentLoader/ContentLoader";
import { PopupYesNo } from "../../notify/Popup";
import { LightboxEntry } from "../Lightbox";

export const Image = ({
    src,
    srcFile,
    busy,
    size,
    rounded,
    squared,
    framed,
    icon,
    onClick,
    onRemove,
    onFileSelect,
    multiple = false,
    iconColor,
    alt,
    grayscale,
    lightbox,
    className
}) => {
    const previousSrc = usePreviousValue(src);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const imageRef = useRef(null);
    const fileSelectorRef = useRef();
    const isMountedRef = useIsMounted();
    const [imageUrl, setImageUrl] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [promptDelete, setPromptDelete] = useState(false);

    useEffect(() => {
        // Wait for the component to be mounted and the image ref to be set
        if (!src || !isMountedRef.current || !imageRef?.current || previousSrc === src) {
            return;
        }

        const { thumbUrl, displayUrl } = getImageSizeUrls(src, imageRef.current);
        if (thumbUrl !== imageUrl) {
            setError(false);
            setLoaded(false);
        }

        setImageUrl(thumbUrl);
        setLightboxUrl(displayUrl);
    }, [src, isMountedRef, imageUrl]);

    useEffect(() => {
        if (!srcFile) {
            return;
        }

        const preview = URL.createObjectURL(srcFile);
        setImageUrl(preview);
        setLightboxUrl(preview);
        return () => {
            console.log("revoke effect:");
            URL.revokeObjectURL(imageUrl);
        };
    }, [srcFile]);

    useEffect(() => {
        if (!srcFile && !src) {
            setImageUrl(null);
            setLightboxUrl(null);
            setError(false);
            setLoaded(false);
        }
    }, [src, srcFile]);

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else if (onFileSelect) {
            fileSelectorRef.current.open();
        }
    };

    const element = (
        <div
            ref={imageRef}
            className={classNames(
                style.base,
                {
                    // Sizes
                    [style.tiny]: size === "tiny",
                    [style.small]: size === "small",
                    [style.medium]: size === "medium",
                    [style.large]: size === "large",
                    [style.xlarge]: size === "xlarge",
                    [style.fill]: size === "fill",
                    [style.fit]: size === "fit",
                    [style.actual]: size === "actual",

                    // Styles
                    [style.rounded]: rounded,
                    [style.squared]: squared,
                    [style.framed]: framed,
                    [style.grayscale]: grayscale,

                    // Icon color
                    [style.iconPrimary]: iconColor === "primary",
                    [style.iconSuccess]: iconColor === "success",
                    [style.iconWarning]: iconColor === "warning",
                    [style.iconError]: iconColor === "error",

                    // Clickable
                    [style.clickable]: onClick || onFileSelect || lightbox,
                    [style.dashed]: onFileSelect && !src,

                    [style.hasSource]: imageUrl,
                    [style.actualLoading]: imageUrl && !loaded && size === "actual"
                },
                className
            )}
            style={{
                width: typeof size === "number" ? `${size}px` : null,
                height: typeof size === "number" ? `${size}px` : null
            }}
            onClick={handleClick}
        >
            {/* Remove button */}
            {onRemove && imageUrl && !busy ? (
                <Clickable className={style.remove} onClick={(e) => e.stopPropagation() ?? setPromptDelete(true)}>
                    <Icon name="close" className={style.icon} />
                </Clickable>
            ) : null}

            {/* Prompt delete */}
            {promptDelete ? (
                <PopupYesNo
                    title={<FormattedMessage id="image.deleteTitle" />}
                    onYes={() => {
                        setPromptDelete(false);
                        onRemove();
                    }}
                    onNo={() => setPromptDelete(false)}
                >
                    <FormattedMessage id="image.deleteConfirm" />
                </PopupYesNo>
            ) : null}

            {/* Wrapper */}
            <div className={style.wrapper}>
                {/* Loading */}
                {busy || (imageUrl && !loaded) ? (
                    <div className={style.busy}>
                        <ContentLoader />
                    </div>
                ) : null}

                {/* Image */}
                {imageUrl && !error ? (
                    <img
                        alt={alt}
                        loading="lazy"
                        draggable={false}
                        src={imageUrl}
                        onLoad={() => setLoaded(true)}
                        onError={() => {
                            setLoaded(true);
                            setError(true);
                        }}
                    />
                ) : null}

                {/* Icon placeholder */}
                {!imageUrl && !busy ? (
                    <Icon className={style.icon} name={!icon || icon === true ? "image" : icon} />
                ) : null}

                {/* Error */}
                {error ? <div className={style.error}></div> : null}
            </div>

            {/* File select */}
            {onFileSelect ? (
                <FileSelector
                    ref={fileSelectorRef}
                    onSelect={(files) => onFileSelect(multiple ? files : files?.[0])}
                    multiple={multiple}
                    accept={["IMAGES"]}
                />
            ) : null}
        </div>
    );

    return lightbox && lightboxUrl ? (
        <LightboxEntry src={lightboxUrl} overlay={<span>{alt}</span>}>
            {element}
        </LightboxEntry>
    ) : (
        element
    );
};

Image.propTypes = {
    // Image url
    src: PropTypes.string,

    // Icon placeholder
    icon: PropTypes.string,

    // Size of avatar, either a pixel number or a size value (medium|large|fill)
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    // Render a squared avatar
    squared: PropTypes.bool
};
